import { useEffect, useState } from 'react';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import ConfirmDialog from '../../components/ConfirmDialog';

const ROLES = ['user', 'checker', 'approver', 'it_support', 'admin'];
const EMPTY_FORM = { name: '', email: '', password: '', role: 'user' };

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);

  const fetchUsers = () => api.get('/users').then(r => setUsers(r.data));
  useEffect(() => { fetchUsers(); }, []);

  const handleRoleChange = async (id, role) => {
    try {
      await api.patch(`/users/${id}/role`, { role });
      toast.success('Role updated');
      fetchUsers();
    } catch { toast.error('Failed to update role'); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${confirmId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch { toast.error('Failed to delete user'); }
    finally { setConfirmId(null); }
  };

  const handleAddUser = async e => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post('/users', form);
      toast.success('User created');
      setShowAdd(false);
      setForm(EMPTY_FORM);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to create user'); }
    finally { setAdding(false); }
  };

  return (
    <div>
      {confirmId && (
        <ConfirmDialog
          message="Are you sure you want to delete this user? This action cannot be undone."
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setConfirmId(null)}
        />
      )}

      {/* Add User Modal */}
      {showAdd && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <div style={S.modalHead}>
              <span style={S.modalTitle}>Add New User</span>
              <button onClick={() => { setShowAdd(false); setForm(EMPTY_FORM); }} style={S.closeBtn}>✕</button>
            </div>
            <form onSubmit={handleAddUser}>
              {[['Name', 'name', 'text'], ['Email', 'email', 'email'], ['Password', 'password', 'password']].map(([label, key, type]) => (
                <div key={key} style={S.field}>
                  <label style={S.label}>{label}</label>
                  <input
                    type={type}
                    required
                    value={form[key]}
                    onChange={e => setForm({ ...form, [key]: e.target.value })}
                    style={S.input}
                    placeholder={label}
                  />
                </div>
              ))}
              <div style={S.field}>
                <label style={S.label}>Role</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={S.input}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <button type="submit" disabled={adding} style={{ ...S.addBtn, opacity: adding ? 0.6 : 1, marginTop: 8 }}>
                {adding ? 'Creating…' : 'Create User'}
              </button>
            </form>
          </div>
        </div>
      )}
      <div style={S.banner}>
        <div style={S.bannerBg} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={S.bannerTitle}>Manage Users</div>
          <div style={S.bannerSub}>Update roles or remove users from the system</div>
        </div>
        <div style={S.blob} />
      </div>

      <div style={S.card}>
        <div style={S.cardTop}>
          <div style={S.cardTitle}>All Users</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={S.badge}>{users.length} users</div>
            <button style={S.addBtn} onClick={() => setShowAdd(true)}>+ Add User</button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.thead}>
                {['Name', 'Email', 'Role', 'SSO Provider', 'Joined', 'Actions'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={{ ...S.tr, background: i % 2 === 0 ? '#fff' : '#f8faff' }}>
                  <td style={{ ...S.td, fontWeight: 700, color: '#0B1554' }}>{u.name}</td>
                  <td style={S.td}>{u.email}</td>
                  <td style={S.td}>
                    <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)} style={S.select}>
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td style={S.td}>
                    {u.sso_provider
                      ? <span style={S.ssoBadge}>{u.sso_provider}</span>
                      : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                  <td style={{ ...S.td, color: '#94a3b8' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td style={S.td}>
                    <button onClick={() => setConfirmId(u.id)} style={S.deleteBtn}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const S = {
  banner: {
    background: 'linear-gradient(135deg,#060d3a 0%,#0d1a6e 50%,#1a0a4a 100%)',
    borderRadius: 18, marginBottom: 24, padding: '32px 36px',
    position: 'relative', overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(6,13,58,0.25)',
  },
  bannerBg: { position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 80% 50%,rgba(79,110,247,0.2) 0%,transparent 60%)' },
  bannerTitle: { color: '#fff', fontSize: 24, fontWeight: 800, marginBottom: 8, letterSpacing: -0.3 },
  bannerSub: { color: 'rgba(255,255,255,0.6)', fontSize: 14 },
  blob: { position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'rgba(79,110,247,0.12)', top: -80, right: 60, filter: 'blur(30px)' },
  card: { background: '#fff', borderRadius: 18, boxShadow: '0 4px 20px rgba(11,21,84,0.06)', overflow: 'hidden', border: '1px solid rgba(79,110,247,0.06)' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #f1f5f9' },
  cardTitle: { fontSize: 14, fontWeight: 800, color: '#0B1554' },
  badge: { background: '#f0f4ff', color: '#4f6ef7', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'linear-gradient(135deg,#060d3a,#0d1a6e)' },
  th: { padding: '13px 18px', color: '#F7D506', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.8 },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '13px 18px', fontSize: 13.5, color: '#374151' },
  select: { padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13, color: '#0B1554', background: '#f9fafb', cursor: 'pointer' },
  deleteBtn: { background: 'linear-gradient(135deg,#dc2626,#ef4444)', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, boxShadow: '0 2px 8px rgba(220,38,38,0.3)' },
  addBtn: { background: 'linear-gradient(135deg,#4f6ef7,#6366f1)', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700, boxShadow: '0 2px 8px rgba(79,110,247,0.3)' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(6,13,58,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 18, padding: '28px 32px', width: 400, boxShadow: '0 16px 48px rgba(6,13,58,0.25)' },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  modalTitle: { fontSize: 16, fontWeight: 800, color: '#0B1554' },
  closeBtn: { background: '#f1f5f9', border: 'none', color: '#64748b', width: 30, height: 30, borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  field: { marginBottom: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 },
  input: { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13.5, boxSizing: 'border-box', color: '#0B1554', background: '#f9fafb' },
};

export default ManageUsers;
